import { logger } from '@/lib/logger'
import { revalidateTag } from 'next/cache'

/**
 * Enhanced fetch implementation specifically designed for GraphQL requests with Next.js cache handling.
 *
 * Key features:
 * - Prevents caching of authenticated requests (requests with Authorization headers)
 * - Automatically detects GraphQL errors in successful responses (HTTP 200)
 * - Revalidates cache tags when GraphQL errors are detected
 * - Preserves the original response regardless of GraphQL error state
 *
 * Use this instead of the native fetch when working with GraphQL endpoints
 * to ensure proper cache behavior based on both HTTP status and GraphQL errors.
 *
 * @param input - The URL or Request object to fetch
 * @param initRaw - Optional fetch configuration (headers, method, etc.)
 * @returns The original fetch Response
 */
export async function customFetch(input: RequestInfo | URL, initRaw?: RequestInit): Promise<Response> {
  let init = { ...initRaw }

  if (init?.headers) {
    const headers = init.headers
    if (typeof headers === 'object' && headers !== null && ('Authorization' in headers || 'authorization' in headers)) {
      init = { ...init, next: undefined }
    }
  }

  const response = await fetch(input, init)

  // If it's a client-side request, no need to worry about cache revalidation
  if (typeof window !== 'undefined') {
    return response
  }

  // If it's not a 200, Next data cache will throw it away anyway.
  if (!response.ok) {
    return response
  }

  try {
    // Clone the response so we can read it without consuming the original
    const clonedResponse = response.clone()
    const result = await clonedResponse.json()

    if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
      console.warn('GraphQL errors detected, revalidating cache tags:', result.errors)
      logger.warn({ errors: result.errors }, 'graphQL errors detected')

      const tags = init?.next?.tags || []
      for (const tag of tags) {
        // Only revalidate the tag unique to the request
        // so we don't revalidate other queries with the same
        // shared tags
        if (tag.startsWith('operation-')) {
          let operationDetails
          if (typeof init?.body === 'string') {
            try {
              const { operationName, variables } = JSON.parse(init.body)
              operationDetails = { operationName, variables }
            } catch {
              operationDetails = init.body
            }
          }
          console.log(`Revalidating tag: ${tag} with operation details: ${JSON.stringify(operationDetails)}`)
          logger.debug({ tag, operationDetails }, 'revalidating tag')
          revalidateTag(tag)
        }
      }
    }
  } catch (e) {
    console.error('Error checking GraphQL response:', e)
  }

  return response
}
